import * as React from "react";
import { createContext, useContext, useEffect, useState } from "react";

interface WindowLocationContextType {
  hashParamsMap: URLSearchParams;
  searchParamsMap: URLSearchParams;
  url: URL;
  replaceHashParam: (key: string, value: string) => void;
  navigate: (url: string) => void;
}

export const WindowLocationContext = createContext<WindowLocationContextType>(null);

export const WindowLocationProvider = ({
  children,
}: React.PropsWithChildren) => {

  const initialUrl = new URL(window.location.href);

  const [hashParamsMap, setHashParamsMap] = useState<URLSearchParams>(new URLSearchParams(initialUrl.hash.slice(1)));
  const [searchParamsMap, setSearchParamsMap] = useState<URLSearchParams>(initialUrl.searchParams);
  const [url, setURL] = useState<URL>(initialUrl);

  useEffect(() => {
    const handleHashChange = () => {
      const url = new URL(window.location.href);
      setURL(url);
      setSearchParamsMap(url.searchParams);
      setHashParamsMap(new URLSearchParams(url.hash.slice(1)));
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const replaceHashParam = (key: string, value: string) => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }

    window.location.hash = params.toString();
  };

  const navigate = (url: string) => {
    window.location.href = url;
  };

  const contextValue = {
    url,
    hashParamsMap,
    searchParamsMap,
    replaceHashParam,
    navigate,
  };

  return (
    <WindowLocationContext.Provider value={contextValue}>
      {children}
    </WindowLocationContext.Provider>
  );
};

export function useWindowLocation() {
  const context = useContext(WindowLocationContext);

  if (!context) {
    throw new Error(
      "useWindowLocation must be used within a WindowLocationProvider"
    );
  }

  return context;
}
